import React from "react";
import IDepositFiatSelect from "./IDepositFiatSelect";
import * as style from "./DepositFiatSelectStyle.module.scss"
import Notice from "@appComponents/Notice/Notice";
import { classNames } from "@features/utils/reactFunctions";
import * as elements from "@appStyles/_elements.module.scss";
import { toUpper } from "@features/utils/functions";
// import ToolTip from '@appComponents/ToolTip/ToolTip';
// import QuestionMark from '@appComponents/QuestionMark/QuestionMark';

const cryptoSelectOptions = [
  {
    label: "USDT Ethereum (ERC-20)",
    value: "usdt-ethereum",
  },
];

export const fiatSelectOptions = [
  {
    label: "AUD (Auto-Converted into USD and then USDT)",
    value: "aud",
  },
  {
    label: "CAD (Auto-Converted into USD and then USDT)",
    value: "cad",
  },
  {
    label: "CNH (Auto-Converted into USD and then USDT)",
    value: "cnh",
  },
  {
    label: "EUR (Auto-Converted into USD and then USDT)",
    value: "eur",
  },
  {
    label: "GBP (Auto-Converted into USD and then USDT)",
    value: "gbp",
  },
  {
    label: "HKD (Auto-Converted into USD and then USDT)",
    value: "hkd",
  },
  {
    label: "JPY (Auto-Converted into USD and then USDT)",
    value: "jpy",
  },
  {
    label: "SGD (Auto-Converted into USD and then USDT)",
    value: "sdg",
  },
  {
    label: "USD (Auto-Converted into USDT)",
    value: "usd",
  },
];

const swiftDoc = `${process.env.S3_PUBLIC_DOCUMENTS_ROOT_URL}pdfs/deposits-swift.pdf`;

const DepositFiatSelect: React.FunctionComponent<IDepositFiatSelect> = (
  props
) => {
  const { network, onRadioChange } = props;

  const renderSelect = (options: { label: string; value: string }[]) => {
    return options.map((el, i) => {
      const checked = network === el.value;
      return (
        <div className={classNames(elements.customRadio, style.radio)} key={i}>
          <input
            value={el.value}
            checked={checked}
            onChange={onRadioChange}
            id={"networkType" + toUpper(el.label)}
            type="radio"
            name="networkType"
          />
          <label htmlFor={"networkType" + toUpper(el.label)}>{el.label}</label>
        </div>
      );
    });
  };

  return (
    <div className={style.depositFiatSelect}>
      <h3 className={style.headline}>
        USDT (ERC-20 on Ethereum mainnet network)
      </h3>
      <Notice
        color={"green"}
        noDivider={true}
        additionalClassName={style.notice}
      >
        Fastest deposit method. Normally arrives within 15 minutes (subject to
        relevant blockchain load).
      </Notice>
      {renderSelect(cryptoSelectOptions)}
      <div id="fiat" className={style.fiatOptions}>
        <h3 className={style.headline}>Fiat (bank transfer)</h3>
        {!props.fiatDisabled && (
          <div>
            <Notice
              color={"orange"}
              noDivider={true}
              additionalClassName={style.notice}
            >
              <b>Important Fiat Deposit and Withdrawal Information</b>
              <br />
              <ol>
                <li>
                  <b>Supported Currencies and Conversion Process:</b>
                  <br />
                  We accept SWIFT bank transfers in USD, SGD, GBP, AUD, HKD,
                  JPY, CNH, EUR, and CAD. Deposits usually clear within 7-14
                  business days. For non-USD deposits, our partner bank converts
                  the funds to USD at the prevailing exchange rate and charges a
                  fee, disclosed post-conversion. BF does not charge a
                  conversion fee; the net USD amount is credited to your
                  account.
                </li>
                <li>
                  <b>Conversion to USDT and Currency Risk:</b>
                  <br />
                  We will automatically convert your USD balance to USDT at the
                  prevailing market rate if it's below 1 USD = 1 USDT. Please
                  note that the USD-USDT exchange rate may fluctuate, and you
                  assume currency conversion risks. The exact USDT amount
                  credited will be determined during processing.
                </li>
                <li>
                  <b>Intermediate Service Deposits:</b>
                  <br />
                  We cannot accept deposits made through Virtual Banks or
                  Remittance Agents (such as Wise, Revolut). Our partner bank
                  requires the sender's identification to be included in the
                  transaction data for compliance reasons.
                </li>
                <li>
                  <b>Required Documentation Upon Deposit Receipt:</b>
                  <br />
                  Upon receipt of your deposit, our partner bank will need the
                  following documents for confirmation and processing:
                  <ul>
                    <li>
                      <b>Relationship Proof</b> verifying your connection with
                      BF Virtual Assets (our team will provide an electronic
                      form for your signature or you can{" "}
                      <a
                        href="https://signnow.com/s/ctXmFq1P?name_formula=RelationshipProof%20-%20FDT%20-%20BF%20Virtual%20Assets%20%7CFull%20Name%201%7CDate%20and%20Time%201"
                        target="_blank"
                      >
                        sign it in advance here
                      </a>
                      )
                    </li>
                    <li>
                      <b>Purpose of Deposit</b>
                    </li>
                    <li>
                      <b>Source of Funds (with supporting documentation)</b>
                    </li>
                    <li>
                      <b>
                        Latest Proof of Address (POA) and Proof of Identity
                        (POI) dated within 2 months of the deposit
                      </b>
                    </li>
                    <li>
                      <b>Deposit Slip from your bank</b>
                    </li>
                  </ul>
                </li>
              </ol>
              <p>
                Our customer service team will reach out to collect these
                documents. <b>Please expect potential delays</b> during this
                process, as we transmit your documentation to our partner bank.
                We are working on improvements to make this process smoother and
                more automated.
              </p>
              <ol start={5}>
                <li>
                  <b>Withdrawal Fees:</b>
                  <ul>
                    <li>
                      <b>Crypto Withdrawals:</b> No withdrawal fees apply to
                      crypto funds, except for applicable Blockchain network
                      fees.
                    </li>
                    <li>
                      <b>Fiat Withdrawals:</b> A 1% fee applies to fiat
                      withdrawals, with a minimum fee of 100 USD. For full
                      details, please refer to our{" "}
                      <a
                        href="https://home.bnktothefuture.com/fees/"
                        target="_blank"
                      >
                        fees page
                      </a>
                      .
                    </li>
                  </ul>
                </li>
              </ol>
            </Notice>
            {renderSelect(fiatSelectOptions)}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(DepositFiatSelect);
